import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { AlertTriangle, CheckCircle2, Settings } from "lucide-react";
import { Link } from "react-router-dom";

const EquipmentSOPs = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Equipment SOPs" 
        description="Standard operating procedures for pool equipment handling"
      >
        <section className="space-y-8">
          {/* Overview */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-3">Purpose</h2>
            <p className="text-muted-foreground">
              These SOPs ensure equipment is left in proper working condition after every service visit. 
              Following these procedures prevents issues like misconfigured valves, accidental mode changes, 
              or equipment damage that could affect the client's pool operation.
            </p>
          </div>

          {/* Pre-Service Check */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Pre-Service Equipment Check</h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Before starting any work on the equipment pad, do a quick visual inspection:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Note actuator toggle positions (should all be ON)</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Observe current valve positions</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Check filter pressure gauge</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Listen for unusual pump sounds</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Note any visible leaks or issues</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Take a photo if something looks off</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4 italic">
                This takes 30 seconds and catches issues before you're responsible for them.
              </p>
            </div>
          </div>

          {/* Actuator Verification */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Settings className="h-6 w-6 text-primary" />
              Actuator Toggle Verification
            </h2>
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-6 mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-destructive mb-2">Critical Procedure</p>
                  <p className="text-muted-foreground">
                    Actuator toggle switches must be verified ON after every service visit. A toggle 
                    accidentally switched OFF can cause serious issues — like a spa draining when 
                    the client activates Spa Mode.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-4">During Service</h4>
              <ul className="space-y-2 text-muted-foreground mb-6">
                <li>• Know where actuators are on each equipment pad</li>
                <li>• Avoid bracing on or near actuators</li>
                <li>• Toggle switches are small and easily bumped — be aware</li>
                <li>• If you need to reach over actuators, do so carefully</li>
              </ul>
              <h4 className="font-semibold text-foreground mb-4">Before Leaving</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Visually confirm all actuator toggles are ON</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />For pool/spa combos: cycle Spa Mode to verify valve rotation</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Visual confirmation that water returns to correct body</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Return to Pool Mode and verify</li>
              </ul>
            </div>
          </div>

          {/* Post-Service Verification */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Post-Service Verification Checklist</h2>
            <div className="bg-card border-2 border-primary rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Complete this checklist before leaving any property:
              </p>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Pump & Filter</h4>
                  <ul className="space-y-1 text-muted-foreground">
                    <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Pump running normally (no air, no cavitation)</li>
                    <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Pump lid secure with O-ring seated</li>
                    <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Filter pressure noted in app</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Valves & Actuators</h4>
                  <ul className="space-y-1 text-muted-foreground">
                    <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />All actuator toggles in ON position</li>
                    <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Valves in correct baseline position</li>
                    <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Pool/Spa mode tested (if applicable)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Baskets & Lids</h4>
                  <ul className="space-y-1 text-muted-foreground">
                    <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Skimmer basket(s) in place</li>
                    <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Pump basket in place</li>
                    <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />All lids secure</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Automation</h4>
                  <ul className="space-y-1 text-muted-foreground">
                    <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />System in correct mode (Pool/Auto)</li>
                    <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Schedules not accidentally changed</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Pool/Spa Testing Protocol */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Pool/Spa Mode Testing Protocol</h2>
            <p className="text-muted-foreground mb-4">
              For any pool with a spa and automated valves, test the mode switching before leaving:
            </p>
            <div className="bg-card border rounded-lg p-6">
              <ol className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">1</span>
                  <span>Activate <strong className="text-foreground">Spa Mode</strong> from automation panel or app</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">2</span>
                  <span>Watch/listen for actuator rotation (usually takes 30-60 seconds)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">3</span>
                  <span><strong className="text-foreground">Visually confirm</strong> water is returning to spa jets, not pool</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">4</span>
                  <span>Switch back to <strong className="text-foreground">Pool Mode</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">5</span>
                  <span>Verify valve rotates back and pool returns are flowing</span>
                </li>
              </ol>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">If the actuator doesn't rotate:</strong> Check toggle 
                  switch position. If it's ON and still not working, document the issue and notify the office.
                </p>
              </div>
            </div>
          </div>

          {/* Baseline Documentation */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Baseline Documentation</h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Each pool/spa system should have documented standard settings. This helps quickly 
                identify if something has changed.
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Take reference photos of correct actuator positions on first visit</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Note which valves control Pool vs Spa mode</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Document filter baseline PSI after cleaning</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Note any unique configurations or equipment</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Use photos as quick reference on future visits</li>
              </ul>
            </div>
          </div>

          {/* When to Report Issues */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">When to Report Issues</h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Document and notify the office immediately if you observe:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li><AlertTriangle className="h-4 w-4 text-amber-600 inline mr-2" />Actuator not responding to automation commands</li>
                <li><AlertTriangle className="h-4 w-4 text-amber-600 inline mr-2" />Unusual actuator sounds (grinding, clicking without movement)</li>
                <li><AlertTriangle className="h-4 w-4 text-amber-600 inline mr-2" />Toggle found in unexpected position (OFF when it should be ON)</li>
                <li><AlertTriangle className="h-4 w-4 text-amber-600 inline mr-2" />Valve stuck or difficult to move manually</li>
                <li><AlertTriangle className="h-4 w-4 text-amber-600 inline mr-2" />Pump not priming after service</li>
                <li><AlertTriangle className="h-4 w-4 text-amber-600 inline mr-2" />Equipment running differently than last visit</li>
                <li><AlertTriangle className="h-4 w-4 text-amber-600 inline mr-2" />Any leaks or equipment damage</li>
              </ul>
            </div>
          </div>

          {/* Related */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Related Topics</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/wiki/equipment/actuators" className="block p-4 bg-card border rounded-lg hover:border-primary transition-colors">
                <span className="font-medium">Valves & Actuators</span>
                <p className="text-sm text-muted-foreground mt-1">Detailed actuator reference</p>
              </Link>
              <Link to="/wiki/procedures/service-workflow" className="block p-4 bg-card border rounded-lg hover:border-primary transition-colors">
                <span className="font-medium">Service Workflow</span>
                <p className="text-sm text-muted-foreground mt-1">Complete visit procedure</p>
              </Link>
            </div>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default EquipmentSOPs;